/**
 * Voice Stack publisher — app side of the locked Brand #26 contract.
 *
 * Kit maps phases onto the existing Orb. We only publish { phase, level }.
 * Four phases. No activated. No fake pulse.
 *
 *   window.dispatchEvent(new CustomEvent('hottr:hottie:voice', {
 *     detail: { phase, level },
 *   }))
 *
 * idle = Hottie is OPEN and there is no audio (level 0).
 * When Hottie closes, stop dispatching — do not keep publishing idle.
 */

export const HOTTIE_VOICE_EVENT = 'hottr:hottie:voice';

export const HOTTIE_VOICE_PHASES = ['idle', 'listening', 'thinking', 'speaking'] as const;

export type HottieVoicePhase = (typeof HOTTIE_VOICE_PHASES)[number];

export type HottieVoiceDetail = {
  phase: HottieVoicePhase;
  /** 0..1 RMS from a real AnalyserNode. */
  level: number;
};

export function isHottieVoicePhase(value: unknown): value is HottieVoicePhase {
  return typeof value === 'string' && (HOTTIE_VOICE_PHASES as readonly string[]).includes(value);
}

export function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(1, value));
}

/**
 * Time-domain RMS of an AnalyserNode, mapped onto 0..1.
 *
 * Speech peaks sit well below 1.0; a modest gain keeps the Orb alive without
 * inventing a pulse. The value still comes from the analyser, never a timer.
 */
export const RMS_GAIN = 4;

export function rmsFromAnalyser(analyser: AnalyserNode, buffer: Float32Array): number {
  analyser.getFloatTimeDomainData(buffer as Float32Array<ArrayBuffer>);
  let sum = 0;
  for (let i = 0; i < buffer.length; i += 1) {
    const sample = buffer[i] ?? 0;
    sum += sample * sample;
  }
  const rms = buffer.length > 0 ? Math.sqrt(sum / buffer.length) : 0;
  return clamp01(rms * RMS_GAIN);
}

export function publishHottieVoice(
  phase: HottieVoicePhase,
  level = 0,
  target: EventTarget | null = typeof window === 'undefined' ? null : window,
): void {
  if (!target) return;
  const rms = phase === 'idle' || phase === 'thinking' ? 0 : clamp01(level);
  target.dispatchEvent(
    new CustomEvent<HottieVoiceDetail>(HOTTIE_VOICE_EVENT, {
      detail: { phase, level: rms },
    }),
  );
}

export function startAnalyserPump(
  analyser: AnalyserNode,
  phase: Extract<HottieVoicePhase, 'listening' | 'speaking'>,
  target?: EventTarget | null,
): () => void {
  const buffer = new Float32Array(analyser.fftSize);
  let raf = 0;
  let stopped = false;

  const tick = () => {
    if (stopped) return;
    publishHottieVoice(phase, rmsFromAnalyser(analyser, buffer), target);
    raf = requestAnimationFrame(tick);
  };

  raf = requestAnimationFrame(tick);
  return () => {
    stopped = true;
    if (raf) cancelAnimationFrame(raf);
  };
}

let sharedCtx: AudioContext | null = null;
let audioContextCreations = 0;
let playbackSource: MediaElementAudioSourceNode | null = null;
let stopPlaybackPump: (() => void) | null = null;

function audioContextCtor(): typeof AudioContext | null {
  if (typeof window === 'undefined') return null;
  return (
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext ||
    null
  );
}

/**
 * Unlock the one Voice AudioContext during the Voice-tab gesture.
 * Speak must reuse this — never construct a second context at playback time.
 */
export function unlockHottieAudio(existing?: AudioContext): AudioContext | null {
  if (existing) {
    sharedCtx = existing;
    if (sharedCtx.state === 'suspended') void sharedCtx.resume();
    return sharedCtx;
  }
  if (sharedCtx) {
    if (sharedCtx.state === 'suspended') void sharedCtx.resume();
    return sharedCtx;
  }
  const Ctor = audioContextCtor();
  if (!Ctor) return null;
  sharedCtx = new Ctor();
  audioContextCreations += 1;
  if (sharedCtx.state === 'suspended') void sharedCtx.resume();
  return sharedCtx;
}

export function getHottieAudioContext(): AudioContext | null {
  return sharedCtx;
}

export function hottieAudioContextCreations(): number {
  return audioContextCreations;
}

export function resetHottieAudioContext(): void {
  stopSpeakPlaybackTap();
  const closing = sharedCtx;
  sharedCtx = null;
  audioContextCreations = 0;
  if (closing && typeof closing.close === 'function') {
    try {
      void closing.close();
    } catch {
      /* already closed */
    }
  }
}

/**
 * Tap an existing HTMLAudioElement into a real AnalyserNode and publish
 * speaking levels. Reuses the unlocked context. Never `new AudioContext()`
 * here — a late context stays suspended and the hijack mutes the element.
 */
export function tapSpeakPlayback(audio: HTMLAudioElement): void {
  stopSpeakPlaybackTap();
  const ctx = getHottieAudioContext();
  if (!ctx) return;

  try {
    if (ctx.state === 'suspended') void ctx.resume();
    playbackSource = ctx.createMediaElementSource(audio);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    playbackSource.connect(analyser);
    analyser.connect(ctx.destination);
    stopPlaybackPump = startAnalyserPump(analyser, 'speaking');
  } catch {
    stopPlaybackPump?.();
    stopPlaybackPump = null;
    try {
      if (playbackSource) {
        playbackSource.disconnect();
        playbackSource.connect(ctx.destination);
      }
    } catch {
      /* mouth still plays via the element if the tap never captured it */
    }
  }
}

export function stopSpeakPlaybackTap(): void {
  stopPlaybackPump?.();
  stopPlaybackPump = null;
  try {
    playbackSource?.disconnect();
  } catch {
    /* already disconnected */
  }
  playbackSource = null;
}
